<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <h1 class="page-title">{{ isEdit ? '編輯罐頭回覆' : '新增罐頭回覆' }}</h1>
      <div class="header-actions">
        <router-link :to="{ name: 'ChatDeskCannedReplies' }" class="btn btn-secondary">
          取消
        </router-link>
        <button
          class="btn btn-primary"
          type="submit"
          form="canned-reply-form"
          :disabled="loading || saving"
        >
          {{ saving ? '儲存中...' : (isEdit ? '儲存變更' : '建立') }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="form-card loading-state">載入中...</div>
      <form v-else id="canned-reply-form" class="form-card" @submit.prevent="submitForm">
        <div class="form-grid">
          <label class="form-field">
            <span>標題</span>
            <input v-model.trim="form.title" maxlength="80" required placeholder="例如：營業時間說明">
          </label>
          <label class="form-field">
            <span>範圍</span>
            <select v-model="form.scope" :disabled="isEdit">
              <option value="personal">我的個人罐頭</option>
              <option v-if="canManageTeam" value="team">團隊共用罐頭</option>
            </select>
          </label>
          <label class="form-field">
            <span>訊息類型</span>
            <select v-model="form.messageType" @change="onMessageTypeChange">
              <option value="text">純文字</option>
              <option value="image">圖片</option>
              <option value="file">檔案</option>
              <option value="line_template">LINE 訊息範本</option>
            </select>
          </label>
          <label class="form-field">
            <span>排序</span>
            <input v-model.number="form.sortOrder" type="number" step="1">
          </label>
        </div>

        <template v-if="form.messageType !== 'line_template'">
          <label class="form-field content-field">
            <span>{{ form.messageType === 'text' ? '訊息內容' : '文字說明（選填）' }}</span>
            <textarea
              v-model="form.content"
              rows="6"
              maxlength="5000"
              :required="form.messageType === 'text'"
              placeholder="輸入回覆內容，可插入下方會員變數"
            />
          </label>
          <div class="variable-buttons">
            <span>插入變數：</span>
            <button
              v-for="variable in variables"
              :key="variable.key"
              type="button"
              class="variable-chip"
              @click="insertVariable(variable.key)"
            >{{ variable.label }}</button>
          </div>
          <p class="hint">會員資料不存在時，變數會保留原標記，讓客服送出前確認。</p>
        </template>

        <div v-else class="line-template-field">
          <label class="form-field-label">選擇個人 LINE 訊息範本</label>
          <div v-if="sourceTemplateDeleted" class="template-warning">
            原始訊息範本已刪除，目前仍會使用保存的快照。若要重新綁定，請選擇另一個範本。
          </div>
          <LineMsgTemplatePicker
            audience-filter="personal"
            :model-value="selectedLineTemplate"
            :selected-id="form.lineMsgTemplateId"
            @update:model-value="onLineTemplateSelected"
          />
          <LineMessagePreview
            v-if="selectedLineTemplate"
            v-bind="lineTemplatePreview"
          />
          <label
            v-if="isEdit && form.lineMsgTemplateId && !sourceTemplateDeleted"
            class="refresh-template-toggle"
          >
            <input v-model="form.refreshLineMsgTemplate" type="checkbox">
            儲存時更新為原始範本的最新內容
          </label>
        </div>

        <div v-if="form.messageType === 'image'" class="media-field">
          <ImageUploader
            v-model="form.mediaUrl"
            :upload-fn="uploadImage"
            shape="free"
            hint="上傳罐頭回覆圖片"
          />
        </div>
        <div v-else-if="form.messageType === 'file'" class="media-field">
          <label class="file-picker">
            <span>{{ uploadingFile ? '上傳中...' : '選擇檔案' }}</span>
            <input type="file" :disabled="uploadingFile" @change="onFileSelected">
          </label>
          <div v-if="form.mediaUrl" class="uploaded-file">
            <i class="ri-attachment-2"></i>
            <span>{{ form.fileName || '已上傳檔案' }}</span>
            <button type="button" @click="clearMedia">移除</button>
          </div>
        </div>

        <label class="active-toggle">
          <input v-model="form.isActive" type="checkbox">
          啟用此罐頭回覆
        </label>
      </form>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import authService from '@/helpers/authservice/authService.js';
import { chatdeskService } from '@/api/modules/chatdesk.js';
import ImageUploader from '@/components/ImageUploader.vue';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import LineMessagePreview from '@/components/LineMessagePreview.vue';
import { chatdeskCannedReplyVariables } from '@/utils/chatdeskCannedReply.js';
import { toLineMsgTemplatePreviewData } from '@/utils/lineMsgTemplatePreview.js';

const emptyForm = () => ({
  title: '',
  scope: 'personal',
  content: '',
  messageType: 'text',
  mediaUrl: '',
  fileName: '',
  mimeType: '',
  fileSize: null,
  lineMsgTemplateId: null,
  refreshLineMsgTemplate: false,
  sortOrder: 0,
  isActive: true
});

export default {
  name: 'ChatDeskCannedReplyEditor',
  components: { ImageUploader, LineMsgTemplatePicker, LineMessagePreview },
  data() {
    return {
      loading: false,
      saving: false,
      uploadingFile: false,
      form: emptyForm(),
      selectedLineTemplate: null,
      sourceTemplateDeleted: false,
      variables: chatdeskCannedReplyVariables
    };
  },
  computed: {
    isEdit() {
      return this.$route.name === 'ChatDeskCannedReplyEdit';
    },
    canManageTeam() {
      return authService.hasPermission('chatdesk:manage');
    },
    lineTemplatePreview() {
      return toLineMsgTemplatePreviewData(this.selectedLineTemplate);
    }
  },
  async mounted() {
    if (this.isEdit) await this.loadReply();
  },
  methods: {
    async loadReply() {
      this.loading = true;
      try {
        const response = await chatdeskService.getCannedReply(this.$route.params.id);
        const reply = response?.data;
        if (!reply) throw new Error('找不到罐頭回覆');
        this.form = {
          title: reply.title,
          scope: reply.scope,
          content: reply.content || '',
          messageType: reply.messageType,
          mediaUrl: reply.mediaUrl || '',
          fileName: reply.fileName || '',
          mimeType: reply.mimeType || '',
          fileSize: reply.fileSize === null ? null : Number(reply.fileSize),
          lineMsgTemplateId: reply.lineMsgTemplateId || reply.lineMsgTemplateSnapshot?.id || null,
          refreshLineMsgTemplate: false,
          sortOrder: reply.sortOrder,
          isActive: reply.isActive
        };
        this.selectedLineTemplate = reply.lineMsgTemplateSnapshot || null;
        this.sourceTemplateDeleted = reply.sourceTemplateDeleted === true;
      } catch (error) {
        await Swal.fire('載入失敗', error.response?.data?.message || error.message, 'error');
        await this.$router.replace({ name: 'ChatDeskCannedReplies' });
      } finally {
        this.loading = false;
      }
    },
    insertVariable(key) {
      this.form.content = `${this.form.content || ''}{{${key}}}`;
    },
    onMessageTypeChange() {
      this.clearMedia();
      this.selectedLineTemplate = null;
      this.form.lineMsgTemplateId = null;
      this.form.refreshLineMsgTemplate = false;
      this.sourceTemplateDeleted = false;
      if (this.form.messageType === 'line_template') this.form.content = '';
    },
    onLineTemplateSelected(template) {
      this.selectedLineTemplate = template;
      this.form.lineMsgTemplateId = template?.id || null;
      this.form.refreshLineMsgTemplate = !!template;
      this.sourceTemplateDeleted = false;
    },
    clearMedia() {
      this.form.mediaUrl = '';
      this.form.fileName = '';
      this.form.mimeType = '';
      this.form.fileSize = null;
    },
    async uploadImage(file) {
      const response = await chatdeskService.uploadFile(file);
      this.form.fileName = file.name;
      this.form.mimeType = file.type;
      this.form.fileSize = file.size;
      return response;
    },
    async onFileSelected(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      this.uploadingFile = true;
      try {
        const response = await chatdeskService.uploadFile(file);
        const url = response?.data?.fileUrl || response?.data?.url;
        if (!url) throw new Error('上傳未回傳 URL');
        this.form.mediaUrl = url;
        this.form.fileName = file.name;
        this.form.mimeType = file.type;
        this.form.fileSize = file.size;
      } catch (error) {
        await Swal.fire('上傳失敗', error.response?.data?.message || error.message, 'error');
      } finally {
        this.uploadingFile = false;
      }
    },
    buildPayload() {
      const isTemplate = this.form.messageType === 'line_template';
      const hasMedia = ['image', 'file'].includes(this.form.messageType);
      return {
        title: this.form.title,
        scope: this.form.scope,
        content: isTemplate ? null : (this.form.content || null),
        messageType: this.form.messageType,
        mediaUrl: hasMedia ? (this.form.mediaUrl || null) : null,
        fileName: hasMedia ? (this.form.fileName || null) : null,
        mimeType: hasMedia ? (this.form.mimeType || null) : null,
        fileSize: hasMedia ? this.form.fileSize : null,
        lineMsgTemplateId: isTemplate ? this.form.lineMsgTemplateId : null,
        refreshLineMsgTemplate: isTemplate ? this.form.refreshLineMsgTemplate : false,
        sortOrder: Number(this.form.sortOrder || 0),
        isActive: this.form.isActive
      };
    },
    async submitForm() {
      if (['image', 'file'].includes(this.form.messageType) && !this.form.mediaUrl) {
        await Swal.fire('缺少媒體', '請先上傳圖片或檔案', 'warning');
        return;
      }
      if (this.form.messageType === 'line_template' && !this.form.lineMsgTemplateId) {
        await Swal.fire('尚未選擇範本', '請選擇一個個人 LINE 訊息範本', 'warning');
        return;
      }

      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.isEdit) {
          delete payload.scope;
          await chatdeskService.updateCannedReply(this.$route.params.id, payload);
        } else {
          await chatdeskService.createCannedReply(payload);
        }
        await Swal.fire('儲存成功', '', 'success');
        await this.$router.push({ name: 'ChatDeskCannedReplies' });
      } catch (error) {
        await Swal.fire('儲存失敗', error.response?.data?.message || error.message, 'error');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
.page-container { max-width: 1000px; margin: 0 auto; }
.form-card {
  background: #fff; border-radius: 8px; padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .05);
}
.loading-state { color: #666; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.form-field { display: grid; gap: 6px; font-size: 14px; font-weight: 600; }
.form-field input, .form-field select, .form-field textarea {
  width: 100%; border: 1px solid #ccd4dc; border-radius: 6px; padding: 9px 11px; font: inherit;
}
.content-field { margin-top: 18px; }
.hint { color: #666; font-size: 14px; margin: 8px 0 16px; }
.variable-buttons { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 10px; font-size: 13px; }
.variable-chip { border: 1px solid #b9c8d6; border-radius: 16px; background: #f7fafc; padding: 4px 9px; cursor: pointer; }
.line-template-field { display: grid; gap: 14px; margin-top: 18px; }
.form-field-label { font-size: 14px; font-weight: 600; }
.line-template-field :deep(.phone-frame) { width: min(360px, 100%); }
.template-warning {
  padding: 10px 12px; border: 1px solid #f5c26b; border-radius: 6px;
  background: #fff8e8; color: #8a5a00; font-size: 14px;
}
.refresh-template-toggle { display: flex; align-items: center; gap: 8px; font-size: 14px; }
.media-field { margin-top: 18px; }
.file-picker {
  display: inline-flex; padding: 9px 14px; border: 1px dashed #8aa4bc; border-radius: 6px; cursor: pointer;
}
.file-picker input { display: none; }
.uploaded-file { display: flex; align-items: center; gap: 8px; margin-top: 10px; }
.uploaded-file button { border: 0; background: none; color: #1976d2; cursor: pointer; }
.active-toggle { display: flex; gap: 8px; align-items: center; margin-top: 18px; font-size: 14px; }
.header-actions { display: flex; align-items: center; gap: 10px; }
.btn { display: inline-flex; align-items: center; justify-content: center; padding: 9px 16px; border-radius: 4px; text-decoration: none; border: 0; cursor: pointer; }
.btn-primary { background: #1976d2; color: white; }
.btn-secondary { background: #edf1f5; color: #34495e; }
.btn:disabled { cursor: not-allowed; opacity: 0.65; }
@media (max-width: 576px) {
  .form-grid { grid-template-columns: 1fr; }
  .form-card { padding: 16px; }
}
</style>
